import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button } from '@Comp';
import { CIPC } from '../../../services/requests';
import './style.scss';

@inject('commonStore', 'onboardingStore')
@withRouter
@observer
export default class OnboardingStep2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedBusinessId: null,
            businesses: [],
            loading: true,
            error: null,
            idNumber: null,
        };
    }

    componentDidMount() {
        // Check if we have existing businesses in store
        const { onboardingStore } = this.props;
        if (onboardingStore.businessData.businesses.length > 0) {
            // Load from store
            this.setState({
                businesses: onboardingStore.businessData.businesses,
                selectedBusinessId: onboardingStore.businessData.selectedBusiness?.registrationNumber || null,
                idNumber: onboardingStore.identityData.idNumber,
                loading: false,
            });
        } else {
            // Fetch fresh data
            this.fetchBusinesses();
        }
    }

    fetchBusinesses = async () => {
        // Get ID number from store (not from router state)
        const { onboardingStore } = this.props;
        const idNumber = onboardingStore.identityData.idNumber;

        if (!idNumber) {
            // No ID number - show error but allow skip
            this.setState({
                error: 'No ID number found. Please complete Step 1 first or skip this step.',
                loading: false,
            });
            return;
        }

        this.setState({ loading: true, error: null, idNumber });

        try {
            const response = await CIPC.searchBusinesses({
                idNumber: idNumber,
            });

            // Check for successful response
            if (response.data.success) {
                // Handle case where businesses array exists
                if (response.data.businesses && response.data.businesses.length > 0) {
                    // Save businesses to store
                    onboardingStore.setBusinesses(response.data.businesses);

                    this.setState({
                        businesses: response.data.businesses,
                        loading: false,
                        error: null,
                    });
                } else {
                    // No businesses found (count is 0) - but allow skip
                    this.setState({
                        error: 'Something went wrong. You can skip this step.',
                        loading: false,
                        businesses: [],
                    });
                }
            } else {
                this.setState({
                    error: 'Something went wrong. You can skip this step.',
                    loading: false,
                });
            }
        } catch (error) {
            console.error('Business lookup error:', error);

            this.setState({
                error: 'Something went wrong. You can skip this step.',
                loading: false,
            });
            // Don't throw - allow user to skip
        }
    };

    handleBusinessSelection = (businessId) => {
        const { businesses } = this.state;
        const { onboardingStore } = this.props;

        // Find the selected business
        const selectedBusiness = businesses.find(b => b.registrationNumber === businessId);

        // Save to store
        if (selectedBusiness) {
            onboardingStore.selectBusiness(selectedBusiness);
        }

        this.setState({ selectedBusinessId: businessId });
    };

    handleNext = () => {
        const { selectedBusinessId } = this.state;
        const { onboardingStore } = this.props;

        if (!selectedBusinessId) {
            return;
        }

        // Mark Step 2 as complete in the store
        onboardingStore.completeStep2();

        // Navigate to Step 3 (data is now in store, no need to pass via state)
        this.props.history.push('/home/onboarding/step3');
    };

    handleSkip = () => {
        // Skip Step 2, go to Step 3
        this.props.history.push('/home/onboarding/step3');
    };

    handleBack = () => {
        this.props.history.push('/home/onboarding/step1');
    };

    render() {
        const { selectedBusinessId, businesses, loading, error } = this.state;

        if (loading) {
            return (
                <div className="step-content">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mt-3">Loading your businesses from CIPC...</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="step-content">
                    <h3 className="step-title">Select the business you want to apply with</h3>
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                    <div className="action-buttons">
                        <Button
                            color="secondary"
                            outline
                            onClick={this.handleBack}
                            className="mr-2"
                        >
                            Back
                        </Button>
                        <Button
                            color="secondary"
                            outline
                            onClick={this.handleSkip}
                        >
                            Skip
                        </Button>
                        <Button color="primary" onClick={this.fetchBusinesses}>
                            Retry
                        </Button>
                    </div>
                </div>
            );
        }

        if (businesses.length === 0 && !error) {
            return (
                <div className="step-content">
                    <h3 className="step-title">Select the business you want to apply with</h3>
                    <div className="alert alert-info" role="alert">
                        No businesses found linked to your ID number. You can skip this step.
                    </div>
                    <div className="action-buttons">
                        <Button
                            color="secondary"
                            outline
                            onClick={this.handleBack}
                            className="mr-2"
                        >
                            Back
                        </Button>
                        <Button
                            color="secondary"
                            outline
                            onClick={this.handleSkip}
                        >
                            Skip
                        </Button>
                        <Button color="primary" onClick={this.fetchBusinesses}>
                            Retry
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="step-content">
                <h3 className="step-title">Select the business you want to apply with</h3>
                <p className="step-description">You can always add more businesses later.</p>
                <p className="instruction-text">Choose the business you would like to link</p>

                {/* Business Selection List */}
                <div className="business-list">
                    {businesses.map((business, index) => (
                        <div
                            key={business.registrationNumber || index}
                            className={`business-card ${selectedBusinessId === business.registrationNumber ? 'selected' : ''}`}
                            onClick={() => this.handleBusinessSelection(business.registrationNumber)}
                        >
                            <div className="business-info">
                                <div className="business-name">{business.name}</div>
                                <div className="business-cipc">Registration No: {business.registrationNumber}</div>
                                {business.type && (
                                    <div className="business-type">
                                        Type: {business.type}
                                    </div>
                                )}
                                {business.status && (
                                    <div className="business-status">
                                        Status: <span className={`badge ${business.status === 'Active' ? 'badge-success' : 'badge-secondary'}`}>
                                            {business.status}
                                        </span>
                                    </div>
                                )}
                                {business.registrationDate && (
                                    <div className="business-date">
                                        Registered: {new Date(business.registrationDate).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                            <div className="radio-container">
                                <input
                                    type="radio"
                                    name="businessSelection"
                                    value={business.registrationNumber}
                                    checked={selectedBusinessId === business.registrationNumber}
                                    onChange={() => this.handleBusinessSelection(business.registrationNumber)}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleBack}
                        className="mr-2"
                    >
                        Back
                    </Button>
                    <Button
                        color="secondary"
                        outline
                        onClick={this.handleSkip}
                    >
                        Skip
                    </Button>
                    <Button
                        color="primary"
                        onClick={this.handleNext}
                        disabled={!selectedBusinessId}
                    >
                        Next
                    </Button>
                </div>
            </div>
        );
    }
}
